import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Button,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

function buildPlanName({
  membershipType,
  period,
  pennantType,
  isTwentyFourSeven,
  includedSports = [],
}) {

  if (membershipType === "Gym Only") {
    const accessText = isTwentyFourSeven ? "24/7" : "Limited Hours";
    return `Gym Only ${accessText}`;
  }


  const baseString = `${membershipType} ${period} ${pennantType}`;


  const accessText = isTwentyFourSeven ? "24/7" : "Not 24/7";
  const sportsText = includedSports.length
    ? includedSports.join("/")
    : "No Sports Selected";

  return `${baseString} - ${accessText} ${sportsText}`;
}

function getButtonText(loading, initialData) {
  if (loading) return "Saving...";
  return initialData ? "Update Plan" : "Add Plan";
}

function AddPlanForm({ initialData, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });


  const [membershipType, setMembershipType] = useState("");
  const [period, setPeriod] = useState("");
  const [pennantType, setPennantType] = useState("");
  const [isTwentyFourSeven, setIsTwentyFourSeven] = useState(false);
  const [sports, setSports] = useState({
    squash: false,
    racquetball: false,
    gym: false,
  });


  const [totalPrice, setTotalPrice] = useState("");   
  const [pricePerPayment, setPricePerPayment] = useState(""); 
  const [paymentFrequency, setPaymentFrequency] = useState("");
  const [duration, setDuration] = useState("");
  const [durationHelper, setDurationHelper] = useState("Leave blank for indefinite");

  const [planName, setPlanName] = useState(""); 


  useEffect(() => {
    if (initialData) {
      const perPayment = initialData.price ? (initialData.price / 100).toFixed(2) : "";
      setPricePerPayment(perPayment);
      setPaymentFrequency(initialData.paymentFrequency || "");
      setDuration(initialData.duration || "");
    }
  }, [initialData]);


  useEffect(() => {
    const includedSports = [];
    if (sports.squash) includedSports.push("Squash");
    if (sports.racquetball) includedSports.push("Racquetball");
    if (sports.gym) includedSports.push("Gym");

    const newName = buildPlanName({
      membershipType,
      period,
      pennantType,
      isTwentyFourSeven,
      includedSports,
    });
    setPlanName(newName);
  }, [membershipType, period, pennantType, isTwentyFourSeven, sports]);


  useEffect(() => {
    if (!period || !paymentFrequency) return;

    let newDuration = "";
    if (period === "Annual") {
      if (paymentFrequency === "WEEKLY") {
        newDuration = 52; 
      } else if (paymentFrequency === "MONTHLY") {
        newDuration = 12;
      } else if (paymentFrequency === "DAILY") {
        newDuration = 365;
      } else if (paymentFrequency === "ANNUAL") {
        newDuration = 1;
      }
    } else if (period === "Seasonal") {
      if (paymentFrequency === "WEEKLY") {
        newDuration = 13;
      } else if (paymentFrequency === "MONTHLY") {
        newDuration = 3;
      } else if (paymentFrequency === "DAILY") {
        newDuration = 90;
      } else if (paymentFrequency === "ANNUAL") {
        newDuration = 1;
      } else if (paymentFrequency === "NINETY_DAYS") {
        newDuration = 1;
      }
    }

    if (newDuration) {
      setDuration(newDuration.toString());
    }
  }, [period, paymentFrequency]);


  useEffect(() => {
    if (!duration) {
      setPricePerPayment("");
      return;
    }

    const installments = parseInt(duration, 10);
    if (!installments || !totalPrice) {
      setPricePerPayment("");
      return;
    }


    const total = parseFloat(totalPrice || "0"); 
    if (Number.isNaN(total) || total <= 0) {
      setPricePerPayment("");
      return;
    }


    const perPayment = total / installments;
    setPricePerPayment(perPayment.toFixed(2));

  }, [totalPrice, duration, paymentFrequency]);

  const handlePaymentFrequencyChange = (e) => {
    const val = e.target.value;
    setPaymentFrequency(val);

    switch (val) {
      case "DAILY":
        setDurationHelper("Enter duration in days");
        break;
      case "WEEKLY":
        setDurationHelper("Enter duration in weeks");
        break;
      case "MONTHLY":
        setDurationHelper("Enter duration in months");
        break;
      case "ANNUAL":
        setDurationHelper("Enter duration in years");
        break;
      default:
        setDurationHelper("Leave blank for indefinite");
    }
  };

  const handleSportToggle = (sportKey) => {
    setSports((prev) => ({ ...prev, [sportKey]: !prev[sportKey] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const deleteSquarePlan = httpsCallable(functions, "deleteSquarePlan");
      const createSquarePlan = httpsCallable(functions, "createSquarePlan");
      const updateSquarePlan = httpsCallable(functions, "updateSquarePlan");

      if (initialData?.variationId) {
        await deleteSquarePlan({ planId: initialData.variationId });
      }

      const planPriceCents = Math.round(parseFloat(pricePerPayment || "0") * 100);
      const planId = planName.replace(/\s+/g, "_").toLowerCase();

      const payload = {
        id: planId,
        name: planName,
        price: planPriceCents,
        frequency: paymentFrequency,
        duration: parseInt(duration, 10) || "INDEFINITE",
      };

      if (initialData) {
        await updateSquarePlan(payload);
      } else {
        await createSquarePlan(payload);
      }

      setSnackbar({
        open: true,
        message: initialData ? "Plan updated successfully!" : "Plan created successfully!",
        severity: "success",
      });

      if (onSave) await onSave();
      if (onClose) onClose();
    } catch (error) {
      console.error("Error saving plan:", error.message);
      setSnackbar({
        open: true,
        message: "Failed to save plan. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h4" gutterBottom>
        {initialData ? "Edit Subscription Plan" : "Add a New Subscription Plan"}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Membership Type</InputLabel>
              <Select
                value={membershipType}
                label="Membership Type"
                onChange={(e) => setMembershipType(e.target.value)}
              >
                <MenuItem value="Adult">Adult</MenuItem>
                <MenuItem value="Junior">Junior</MenuItem>
                <MenuItem value="Gym Only">Gym Only</MenuItem>
                <MenuItem value="Sponsor">Sponsor</MenuItem>
                <MenuItem value="Life">Life</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Membership Period</InputLabel>
              <Select
                value={period}
                label="Membership Period"
                onChange={(e) => setPeriod(e.target.value)}
                disabled={membershipType === "Gym Only"}
              >
                <MenuItem value="Annual">Annual</MenuItem>
                <MenuItem value="Seasonal">Seasonal</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Pennant Type</InputLabel>
              <Select
                value={pennantType}
                label="Pennant Type"
                onChange={(e) => setPennantType(e.target.value)}
                disabled={membershipType === "Gym Only"}
              >
                <MenuItem value="Single Pennant">Single Pennant</MenuItem>
                <MenuItem value="Multi Pennant">Multi Pennant</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isTwentyFourSeven}
                  onChange={(e) => setIsTwentyFourSeven(e.target.checked)}
                />
              }
              label="24/7 Access?"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Included Sports (check all that apply):
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sports.squash}
                  onChange={() => handleSportToggle("squash")}
                  disabled={membershipType === "Gym Only"}
                />
              }
              label="Squash"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={sports.racquetball}
                  onChange={() => handleSportToggle("racquetball")}
                  disabled={membershipType === "Gym Only"}
                />
              }
              label="Racquetball"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={sports.gym}
                  onChange={() => handleSportToggle("gym")}
                />
              }
              label="Gym"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              required
              label="Total Price"
              fullWidth
              type="number"
              value={totalPrice}
              onChange={(e) => setTotalPrice(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">A$</InputAdornment>,
              }}
              helperText="Total membership cost for the entire period (e.g., 195.00 for Seasonal)."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Payment Frequency</InputLabel>
              <Select
                value={paymentFrequency}
                label="Payment Frequency"
                onChange={handlePaymentFrequencyChange}
              >
                <MenuItem value="DAILY">Daily</MenuItem>
                <MenuItem value="WEEKLY">Weekly</MenuItem>
                <MenuItem value="MONTHLY">Monthly</MenuItem>
                <MenuItem value="ANNUAL">Annual</MenuItem>
                <MenuItem value="NINETY_DAYS">Seasonal</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Duration"
              fullWidth
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              helperText={durationHelper}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Price Per Payment"
              fullWidth
              value={pricePerPayment} 
              disabled
              InputProps={{
                startAdornment: <InputAdornment position="start">A$</InputAdornment>,
              }}
              helperText="Automatically calculated based on total price and duration."
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Plan Name"
              fullWidth
              value={planName}
              disabled
              helperText="Auto-generated name based on your selections."
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Button type="submit" variant="contained" color="primary" disabled={loading}>
            {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
            {getButtonText(loading, initialData)}
          </Button>
          <Button onClick={onClose} color="secondary" sx={{ ml: 2 }} disabled={loading}>
            Cancel
          </Button>
        </Box>
      </form>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AddPlanForm;
